// Unique, useful prose + FAQ per landing-page context. Gives the otherwise
// thin filter+grid pages real content (helps indexing & head-term rankings)
// and emits FAQPage structured data. Kept deliberately non-spammy: the copy
// should read as genuinely helpful to a cyclist, not keyword stuffing.

import { displayBundesland } from '@radevents/shared';

export interface Faq {
  q: string;
  a: string;
}

export interface LandingContentCtx {
  /** EventCategory: ROAD | GRAVEL | MTB | '' */
  category?: string;
  /** Plural label of a forced event-type page, e.g. 'Radmarathons' */
  eventTypeLabel?: string;
  /** Display Bundesland, e.g. 'Bayern' */
  bundesland?: string;
  /** City name, e.g. 'München' */
  city?: string;
  year: number;
}

export interface LandingContent {
  heading: string;
  prose: string;
  faqs: Faq[];
}

function regionPhrase(ctx: LandingContentCtx): string {
  if (ctx.city) return `in und um ${ctx.city}`;
  if (ctx.bundesland) return `in ${displayBundesland(ctx.bundesland)}`;
  return 'in ganz Deutschland';
}

function topic(ctx: LandingContentCtx): string {
  if (ctx.eventTypeLabel) return ctx.eventTypeLabel;
  if (ctx.category === 'ROAD') return 'Rennrad- und RTF-Events';
  if (ctx.category === 'GRAVEL') return 'Gravel-Events';
  if (ctx.category === 'MTB') return 'MTB-Events';
  return 'Radsport-Events';
}

export function getLandingContent(ctx: LandingContentCtx): LandingContent {
  const region = regionPhrase(ctx);
  const t = topic(ctx);

  const prose =
    `Auf Radsport Events findest du ${t} ${region} für ${ctx.year} – übersichtlich nach Datum, ` +
    `Region und Distanz sortiert. Jede Veranstaltung zeigt dir Termin, Startort, Streckenlängen, ` +
    `Höhenmeter und den direkten Link zur Anmeldung. So planst du deine Saison von der ersten ` +
    `Ausfahrt im Frühjahr bis zu den großen Marathons im Spätsommer.`;

  const faqs: Faq[] = [];

  // Context-specific lead question
  if (ctx.eventTypeLabel) {
    faqs.push({
      q: `Wo finde ich ${ctx.eventTypeLabel} ${region}?`,
      a: `Diese Seite listet alle bei Radsport Events erfassten ${ctx.eventTypeLabel} ${region}. Über die Filter kannst du zusätzlich nach Datum, Distanz und Umkreis eingrenzen.`,
    });
  } else if (ctx.category === 'ROAD') {
    faqs.push({
      q: 'Was ist der Unterschied zwischen RTF und Jedermann-Rennen?',
      a: 'Eine RTF (Radtourenfahrt) ist eine Breitensport-Ausfahrt ohne Wertung auf festen Strecken mit Verpflegung. Ein Jedermann-Rennen wird auf Zeit gefahren und hat eine Ergebnisliste.',
    });
  } else if (ctx.category === 'GRAVEL') {
    faqs.push({
      q: 'Brauche ich ein spezielles Rad für Gravel-Events?',
      a: 'Ein Gravelbike mit etwas breiteren Reifen ist ideal, viele Strecken lassen sich aber auch mit einem robusten Cross- oder Mountainbike fahren. Die Streckeninfos je Event helfen bei der Wahl.',
    });
  } else if (ctx.category === 'MTB') {
    faqs.push({
      q: 'Was bedeutet CTF bei MTB-Events?',
      a: 'CTF steht für Country-Tourenfahrt – eine geführte oder ausgeschilderte MTB-Tour im Breitensport, vergleichbar mit einer RTF, aber im Gelände.',
    });
  } else {
    faqs.push({
      q: `Wie finde ich Radsport-Events ${region}?`,
      a: `Nutze die Filter nach Disziplin (Rennrad, Gravel, MTB), Datum, Distanz und Umkreis. Mit Postleitzahl und Radius findest du Veranstaltungen direkt in deiner Nähe.`,
    });
  }

  // Common, genuinely useful questions
  faqs.push(
    {
      q: 'Sind die Termine aktuell?',
      a: 'Ja. Radsport Events gleicht die Veranstaltungen regelmäßig automatisch mit den Quellen der Veranstalter ab. Abgesagte oder ausgebuchte Events werden entsprechend gekennzeichnet.',
    },
    {
      q: 'Kann ich ein Event zu meinem Kalender hinzufügen?',
      a: 'Ja. Auf jeder Event-Detailseite kannst du den Termin als iCal-Datei (.ics) herunterladen und in Apple Kalender, Google Kalender oder Outlook importieren.',
    },
    {
      q: 'Mein Event fehlt – wie bekomme ich es auf die Seite?',
      a: 'Über „Event vorschlagen" kannst du eine Veranstaltung einreichen. Nach einer kurzen Prüfung erscheint sie kostenlos im Kalender.',
    },
  );

  return {
    heading: `Häufige Fragen zu ${t} ${region}`,
    prose,
    faqs,
  };
}
